import { MainSectionWrapper } from '../../../../pageStructure/mainSectionWrapper/index';
import styles from '../../flexiApprovedView/FlexiApprovedView.module.css';
import rblStyles from './RBLOfferConfigurationView.module.css';
import {
    fetchGetFormalOffers,
    fetchGetMcaEstimatedSchedule,
    GetFormalOffersResponse
} from '@iwoca/lapi-client/edge';
import { FormalOffer } from '../../../MyOffers.types';
import { Button } from '@iwoca/orion';
import { Tooltip } from '../../../../ui/tooltip/Tooltip';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { MoneyNoDecimals } from '../../../../ui/moneyNoDecimals';
import messages from '../../flexiApprovedView/CustomiseOffer.messages';
import { Dispatch } from 'redux';
import { buildLocationWithSearch } from '../../../../drawDown/DrawDownHelper';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { connect } from 'react-redux';
import { XSellTitle } from '../../xsell/xsellTitle/xsellTitle';
import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';
import { tracking } from '@iwoca/frontend-tracking-library';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import { getMaxAvailableCash, getTimeOutDate } from '../../../MyOffers.helpers';
import { RBLLoanSummary } from './RBLLoanSummary/RBLLoanSummary';
import MainContentPending from '../../../../pageStructure/mainContentPending';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import {
    convertSweepToDecimal,
    convertSweepToPercentage
} from './RBLOfferConfiguration.helpers';
import { ChooseLoanAmount } from '../../../../sharedDashboardComponents/slider/chooseLoanAmount/ChooseLoanAmount';
import { RblSweepSelect } from './rblSweepSelect/rblSweepSelect';
import { EstimatedRepayments } from './EstimatedRepayments/EstimatedRepayments';
import Fetching from '../../../../ui/fetching/fetching';
import configurationMessages from './RBLOfferConfigurationView.messages';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    startDrawdown: ({
        amount,
        offerId
    }: {
        amount: number;
        offerId: FormalOffer['offer_id'];
    }) => {
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            //@ts-ignore
            location: buildLocationWithSearch({
                pathname: '/funding-requirements/',
                offerId,
                amount
            })
        });
    }
});

const RBLOfferConfiguration = ({
    startDrawdown
}: ReturnType<typeof mapDispatchToProps>) => {
    const [drawdownAmount, setDrawdownAmount] = useState<number>(0);
    const [selectedOffer, setSelectedOffer] = useState<
        NonNullable<GetFormalOffersResponse['data']>['formal_offers'][0] | null
    >(null);

    const stateKey = useStateKey();
    const intl = useIntl();
    const amountInRange = selectedOffer?.available_cash! >= drawdownAmount;

    const [sweep, setSweep] = useState(0.0);
    const [sweepVals, setSweepVals] = useState({
        absMin: 0,
        absMax: 0,
        min: 0,
        max: 0
    });

    const {
        isLoading,
        isError,
        data: getFormalOffersResponse
    } = useQuery({
        queryKey: ['formal_offers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey: stateKey! })
    });

    const {
        data: schedule,
        isFetching: scheduleLoading,
        error: scheduleError
    } = useQuery({
        queryKey: [
            'mcaSchedule',
            stateKey,
            selectedOffer?.offer_id!,
            drawdownAmount,
            sweep
        ],
        queryFn: () => {
            return fetchGetMcaEstimatedSchedule({
                stateKey: stateKey,
                offerId: selectedOffer?.offer_id!,
                query: {
                    cash_amount:
                        drawdownAmount > 0 ? drawdownAmount : undefined,
                    sweep_fraction:
                        sweep > 0 ? convertSweepToDecimal(sweep) : undefined
                }
            });
        },
        enabled: Boolean(selectedOffer?.offer_id!),
        keepPreviousData: true
    });

    useEffect(() => {
        if (getFormalOffersResponse?.data) {
            setDrawdownAmount(
                getFormalOffersResponse!.data!.formal_offers[0].available_cash!
            );
            setSelectedOffer(getFormalOffersResponse.data.formal_offers[0]);
        }
    }, [getFormalOffersResponse, setDrawdownAmount]);

    if (!isError && (isLoading || drawdownAmount === 0)) {
        return (
            <MainContentPending data-testid="rbl-configuration-view-loading" />
        );
    }

    if (isError || scheduleError) {
        return <FallbackErrorModal />;
    }

    const maxCashAmount = getMaxAvailableCash(
        getFormalOffersResponse!.data!.formal_offers
    );
    const timeoutDate = getTimeOutDate(
        getFormalOffersResponse!.data!.formal_offers
    );

    // // Set intial sweep values
    if (sweep === 0 && schedule) {
        const initialSweepMin = convertSweepToPercentage(
            schedule.data.summary.sweep.minimum_sweep_fraction
        );
        const initialSweepMax = convertSweepToPercentage(
            schedule.data.summary.sweep.maximum_sweep_fraction
        );
        setSweep(initialSweepMin);
        setSweepVals({
            absMin: 0.1,
            absMax: initialSweepMax,
            min: initialSweepMin,
            max: initialSweepMax
        });
    }

    return (
        <section>
            <XSellTitle
                expiryDate={timeoutDate}
                maxCashAmount={maxCashAmount}
            />

            <MainSectionWrapper
                className={styles.mainSection}
                innerWrapperClassName={styles.mainSectionRow}
            >
                <div
                    className={styles.contentColumn}
                    data-testid="new-approval-view-left-column"
                >
                    <ChooseLoanAmount
                        setDrawdownAmount={setDrawdownAmount}
                        drawdownAmount={drawdownAmount}
                        minimumDrawdownAmount={
                            schedule?.data.summary.amount
                                .minimum_transaction_amount!
                        }
                        maximumDrawdownAmount={
                            schedule?.data.summary.amount
                                .maximum_transaction_amount!
                        }
                        inputTooltip={
                            <Tooltip
                                data-action-name="Input tooltip"
                                message={
                                    <FormattedMessage
                                        {...messages.inputTooltip}
                                        values={{
                                            amount: (
                                                <MoneyNoDecimals
                                                    value={
                                                        schedule?.data.summary
                                                            .amount
                                                            .maximum_transaction_amount!
                                                    }
                                                />
                                            ),
                                            date: (
                                                <FormattedDate
                                                    value={
                                                        selectedOffer
                                                            ? new Date(
                                                                  selectedOffer.timeout!
                                                              ).toISOString()
                                                            : new Date()
                                                    }
                                                    day="2-digit"
                                                    month="long"
                                                    year="numeric"
                                                />
                                            )
                                        }}
                                    />
                                }
                            />
                        }
                    />
                    <RblSweepSelect
                        key={selectedOffer!.offer_id}
                        offer={selectedOffer!}
                        drawdownAmount={drawdownAmount}
                        sweep={sweep!}
                        setSweep={setSweep!}
                        schedule={schedule?.data!}
                        sweepVals={sweepVals!}
                        setSweepVals={setSweepVals!}
                    />

                    {schedule && (
                        <EstimatedRepayments
                            className={rblStyles.estimatedSummary}
                            schedule={schedule!}
                            loading={scheduleLoading}
                        />
                    )}

                    <p className={rblStyles.legalText}>
                        <FormattedMessage
                            {...configurationMessages.legalText}
                            values={{
                                period: schedule?.data.estimated_schedule
                                    .estimated_time_to_repay_in_months,
                                bold: (chunks) => <strong>{chunks}</strong>,
                                fetching: (chunks) => (
                                    <Fetching isFetching={scheduleLoading}>
                                        {chunks}
                                    </Fetching>
                                )
                            }}
                        />
                    </p>
                </div>

                <div
                    className={styles.contentColumn}
                    data-testid="new-approval-view-right-column"
                >
                    {selectedOffer && (
                        <RBLLoanSummary
                            selectedOffer={selectedOffer}
                            schedule={schedule}
                            scheduleLoading={scheduleLoading}
                            drawdownAmount={drawdownAmount}
                        />
                    )}

                    <div className={styles.ctaContainer}>
                        <Button
                            variant="success"
                            data-testid="start-drawdown"
                            data-action-name="Approval continue button"
                            disabled={Boolean(!selectedOffer) || !amountInRange}
                            onClick={() => {
                                tracking.ampli.drawdownCtaClicked();

                                startDrawdown({
                                    amount: drawdownAmount,
                                    offerId: selectedOffer!.offer_id
                                });
                            }}
                            className={styles.cta}
                        >
                            <FormattedMessage
                                {...messages.cta}
                                values={{
                                    amount: (
                                        <MoneyNoDecimals
                                            className={styles.summaryAmount}
                                            value={drawdownAmount}
                                        />
                                    )
                                }}
                            />
                        </Button>

                        {selectedOffer &&
                            (amountInRange ? (
                                <div className={styles.ctaHeplerText}>
                                    <FormattedMessage {...messages.CTAinfo} />{' '}
                                </div>
                            ) : (
                                <div className="mt-s text-s font-reg text-error">
                                    <FormattedMessage
                                        {...messages.CTAdisabled}
                                        values={{
                                            availableCash: intlFormatCurrency({
                                                intl,
                                                value: selectedOffer.available_cash!
                                            })
                                        }}
                                    />
                                </div>
                            ))}
                    </div>
                </div>
            </MainSectionWrapper>
        </section>
    );
};

export const RBLOfferConfigurationView = connect(
    null,
    mapDispatchToProps
)(RBLOfferConfiguration);
